import React, { useEffect, useState, useMemo } from 'react'; // Added useMemo
import { collection, getDocs, query, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import './KatalogBahan.css'; // Import the CSS file

// Define a threshold for low stock warning (can be same as laboran monitoring or different)
const LOW_STOCK_THRESHOLD = 10;

// Helper to format Firestore Timestamp to readable date string
const formatDate = (timestamp) => {
  if (timestamp instanceof Timestamp) {
    // Format date only, assuming expiry doesn't need time
    return timestamp.toDate().toLocaleDateString('id-ID');
  }
  return typeof timestamp === 'string' ? timestamp : 'N/A';
};

const KatalogBahan = () => {
  const [bahanList, setBahanList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState(''); // State for search term
  const [selectedImage, setSelectedImage] = useState(null); // State for image popup
  const navigate = useNavigate(); // Hook for navigation

  // Fetch materials
  const fetchBahan = async () => {
    setLoading(true);
    setError('');
    try {
      const bahanRef = collection(db, 'bahan');
      const q = query(bahanRef, orderBy('nama')); // Order by name
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      setBahanList(data);
    } catch (err) {
      console.error("Error fetching materials:", err);
      setError('Gagal memuat data bahan.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBahan();
  }, []);

  // Filtered list based on search term
  const filteredBahanList = useMemo(() => {
    if (!searchTerm) {
      return bahanList;
    }
    return bahanList.filter(bahan =>
      bahan.nama.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (bahan.kode && bahan.kode.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [bahanList, searchTerm]);


  // Function to determine stock status text and class
  const getStockStatus = (stock) => {
    const currentStock = Number(stock);
    if (isNaN(currentStock) || currentStock <= 0) {
      return { text: 'Habis', className: 'stock-out' };
    }
    if (currentStock <= LOW_STOCK_THRESHOLD) {
      return { text: 'Stok Menipis', className: 'stock-low' };
    }
    return { text: 'Tersedia', className: 'stock-available' };
  };

  // Handler for navigation to request form
  const handleAjukanPermintaan = (bahan) => {
    navigate('/dashboard/form-permintaan-bahan', {
      state: { bahanId: bahan.id, namaBahan: bahan.nama }
    });
  };

  // Handler for image click
  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  // Handler to close image popup
  const handleCloseImage = () => {
    setSelectedImage(null);
  };

  return (
    <div className="katalog-bahan-container">
      <h2>Katalog Bahan</h2>

      {error && <div className="error-message">{error}</div>}

      {/* Search Input */}
      <div className="search-container" style={{ marginBottom: '20px', maxWidth: '400px' }}>
        <input
          type="text"
          placeholder="Cari bahan berdasarkan nama atau kode..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
          style={{ width: '100%', padding: '8px 10px', borderRadius: '4px', border: '1px solid #ccc' }}
        />
      </div>

      {loading ? (
        <div className="loading-message">Memuat data bahan...</div>
      ) : (
        <div className="katalog-bahan-table-container">
          <table className="katalog-bahan-table">
            <thead>
              <tr>
                <th>Nama Bahan</th>
                <th>Kode</th>
                <th>Satuan</th>
                <th>Lokasi</th>
                <th>Status Stok</th>
                <th>Tanggal Kadaluarsa</th>
                <th>Gambar</th>
                <th>Dokumen</th>
                <th>Aksi</th> {/* Added Actions Column */}
              </tr>
            </thead>
            <tbody>
              {filteredBahanList.map((bahan) => { // Use filtered list
                const stockStatus = getStockStatus(bahan.stok);
                return (
                  <tr key={bahan.id}>
                    <td>{bahan.nama || 'N/A'}</td>
                    <td>{bahan.kode || 'N/A'}</td>
                    <td>{bahan.satuan || 'N/A'}</td>
                    <td>{bahan.lokasi || 'N/A'}</td>
                    <td>
                      <span className={`stock-status ${stockStatus.className}`}>
                        {stockStatus.text}
                      </span>
                    </td>
                    <td>{formatDate(bahan.expiry)}</td>
                    <td>
                      {bahan.gambarUrl ? (
                        <img 
                          src={bahan.gambarUrl} 
                          alt={bahan.nama} 
                          className="table-image" 
                          onClick={() => handleImageClick(bahan.gambarUrl)}
                          style={{ cursor: 'pointer' }}
                        />
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td>
                      {bahan.dokumenUrl ? (
                        <a href={bahan.dokumenUrl} target="_blank" rel="noopener noreferrer" className="table-link">
                          Lihat Dokumen
                        </a>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td className="action-buttons" style={{ whiteSpace: 'nowrap' }}>
                      {Number(bahan.stok) > 0 && (
                        <button
                          onClick={() => handleAjukanPermintaan(bahan)}
                          className="btn btn-ajukan"
                          style={{ padding: '5px 10px', fontSize: '12px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        >
                          Ajukan
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filteredBahanList.length === 0 && !loading && ( // Check filtered list length
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center' }}>
                     {searchTerm ? 'Tidak ada bahan yang cocok dengan pencarian.' : 'Tidak ada data bahan yang tersedia.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Image Popup Modal */}
      {selectedImage && (
        <div 
          className="image-popup-overlay" 
          onClick={handleCloseImage}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
        >
          <div 
            className="image-popup-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              maxWidth: '90%',
              maxHeight: '90%',
              backgroundColor: 'white',
              padding: '10px',
              borderRadius: '5px'
            }}
          >
            <button 
              onClick={handleCloseImage}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '30px',
                height: '30px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              &times;
            </button>
            <img 
              src={selectedImage} 
              alt="Enlarged view" 
              style={{
                maxWidth: '100%',
                maxHeight: '80vh',
                display: 'block'
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default KatalogBahan;
